// API Configuration for Neurula Health EMR Frontend
// Supports multiple environments and FastAPI backend integration

const getEnvironment = () => {
  // Check for environment variable first
  if (process.env.REACT_APP_ENVIRONMENT) {
    return process.env.REACT_APP_ENVIRONMENT;
  }
  
  // Fallback to hostname detection
  const hostname = window.location.hostname;
  
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return 'development';
  } else if (hostname.includes('staging') || hostname.includes('test')) {
    return 'staging';
  } else {
    return 'production';
  }
};

const environment = getEnvironment();

// Environment-specific configurations
const config = {
  development: {
    API_BASE_URL: process.env.REACT_APP_API_URL || "http://localhost:8000/api/v1",
    WS_URL: process.env.REACT_APP_WS_URL || "ws://localhost:8000/ws",
    TIMEOUT: 30000,
    RETRY_ATTEMPTS: 3,
    RETRY_DELAY: 1000,
    DEBUG: false
  },
  staging: {
    API_BASE_URL: process.env.REACT_APP_API_URL || "https://staging-api.neurula.health/api/v1",
    WS_URL: process.env.REACT_APP_WS_URL || "wss://staging-api.neurula.health/ws",
    TIMEOUT: 30000,
    RETRY_ATTEMPTS: 3,
    RETRY_DELAY: 1000,
    DEBUG: false
  },
  production: {
    API_BASE_URL: process.env.REACT_APP_API_URL || "https://api.neurula.health/api/v1",
    WS_URL: process.env.REACT_APP_WS_URL || "wss://api.neurula.health/ws",
    TIMEOUT: 30000,
    RETRY_ATTEMPTS: 2,
    RETRY_DELAY: 2000,
    DEBUG: false
  }
};

// Export current environment configuration
export const API_BASE_URL = config[environment].API_BASE_URL;
export const WS_URL = config[environment].WS_URL;
export const API_TIMEOUT = config[environment].TIMEOUT;
export const API_RETRY_ATTEMPTS = config[environment].RETRY_ATTEMPTS;
export const API_RETRY_DELAY = config[environment].RETRY_DELAY;
export const DEBUG_MODE = config[environment].DEBUG;
export const ENVIRONMENT = environment;

// Default headers for all API requests
export const DEFAULT_HEADERS = {
  'Content-Type': 'application/json',
  'Accept': 'application/json',
  'X-Requested-With': 'XMLHttpRequest'
};

// Authentication specific configuration
export const AUTH_CONFIG = {
  TOKEN_STORAGE_KEY: 'loginSuccessNeurulaHealth',
  REFRESH_TOKEN_STORAGE_KEY: 'refreshTokenNeurulaHealth',
  TOKEN_REFRESH_THRESHOLD: 5 * 60 * 1000, // 5 minutes before expiry
  MAX_REFRESH_ATTEMPTS: 3,
  AUTO_LOGOUT_ON_TOKEN_EXPIRE: true
};

// API endpoints configuration
export const API_ENDPOINTS = {
  AUTH: {
    LOGIN: '/auth/login',
    LOGOUT: '/auth/logout',
    REFRESH: '/auth/refresh',
    CHANGE_PASSWORD: '/auth/change-password',
    FORGOT_PASSWORD: '/auth/forgot-password',
    RESET_PASSWORD: '/auth/reset-password',
    VERIFY_EMAIL: '/auth/verify-email',
    VALIDATE_TOKEN: '/auth/validate-token',
    SESSION: '/auth/session'
  },
  USERS: {
    PROFILE: '/users/profile',
    STAFF_DETAILS: '/users/staff-details',
    STAFF_LIST: '/users',
    UPDATE_FCM_TOKEN: '/users/update-fcm-token'
  },
  PATIENTS: {
    LIST: '/patients',
    SEARCH: '/patients/search',
    BY_ID: '/patients/{id}',
    BY_MRN: '/patients/mrn/{mrn}',
    PRE_REGISTER: '/patients/pre-register',
    REGISTER: '/patients/register',
    UPDATE: '/patients/{id}',
    ALLERGIES: '/patients/{id}/allergies',
    MEDICATIONS: '/patients/{id}/medications',
    STAFF_ASSIGNMENTS: '/patients/{id}/staff-assignments',
    STATUS: '/patients/{id}/status'
  },
  DASHBOARD: {
    STATISTICS: '/dashboard/statistics',
    SUMMARY: '/dashboard/summary',
    STAFF_SUMMARY: '/dashboard/staff-summary',
    PATIENT_RECORD: '/dashboard/patient-record-dashboard'
  },
  DYNAMIC_FORMS: {
    LIST: '/dynamic-forms',
    SUBMISSIONS: '/dynamic-forms/submissions',
    BY_ID: '/dynamic-forms/{id}',
    SUBMIT: '/dynamic-forms/{id}/submit'
  }
};

// Development utilities
export const isDevelopment = () => environment === 'development';
export const isProduction = () => environment === 'production';
export const isStaging = () => environment === 'staging';

// Development utilities only - removed logging for production
export const isDevelopmentMode = () => environment === 'development' && DEBUG_MODE;